import type {State} from 'src/reducers';
import type {PhoneNumberSet} from 'src/types/chat/phone-number-sets';
import type {QueueBasedInbox} from 'src/types/chat/queues';
import {isQueueInbox, isPhoneInbox} from 'src/selectors/multi-number-inbox';

import {createSelector} from 'reselect';

import {
  selectDefaultPhoneNumberSet,
  selectPrimaryPhoneNumberSet,
  selectFirstPhoneNumberSet,
} from 'src/selectors/phone-number-sets';
import {
  selectPrimaryQueue,
  selectFirstQueue,
} from 'src/selectors/messaging-queues';


export const selectCurrentlySelectedInboxId = (state: State): string => {
  // @ts-expect-error - TS2339 - Property 'selectedPhoneNumberSetId' does not exist on type 'MultiNumberInboxState'.
  return state.chat.multiNumberInbox.selectedPhoneNumberSetId;
};

export const selectCurrentInbox = (
  state: State,
): PhoneNumberSet | QueueBasedInbox => {
  const selectedInboxId = selectCurrentlySelectedInboxId(state);

  const isQueueInboxType = isQueueInbox(state);
  const isPhoneInboxType = isPhoneInbox(state);
  if (isQueueInboxType) {
    return (
      state.chat.queues.queue_based_inboxes[selectedInboxId] ||
      selectPrimaryQueue(state) ||
      selectFirstQueue(state)
    );
  } else if (isPhoneInboxType) {
    return (
      state.chat.phoneNumberSets[selectedInboxId] ||
      selectPrimaryPhoneNumberSet(state) ||
      selectFirstPhoneNumberSet(state)
    );
  } else {
    return (
      state.chat.phoneNumberSets[selectedInboxId] ||
      state.chat.queues.queue_based_inboxes[selectedInboxId] ||
      selectPrimaryPhoneNumberSet(state) ||
      selectPrimaryQueue(state) ||
      selectFirstPhoneNumberSet(state) ||
      selectFirstQueue(state)
    );
  }
};

export const selectCurrentInboxId: (arg1: State) => string = createSelector(
  selectCurrentInbox,
  isQueueInbox,
  (inbox, isQueueInboxType) => {
    if (!inbox) {
      return '';
    }
    if (isQueueInboxType) {
      return (inbox as QueueBasedInbox).id;
    }
    const phoneInbox = inbox as PhoneNumberSet;
    const selected = phoneInbox.selected_channel;
    if (selected === 'sms' || selected === 'whatsapp') {
      return phoneInbox[selected]?.inbox_id || '';
    }
    return '';
  },
);

export const selectCurrentInboxAvailableChannels: (arg1: State) => string[] =
  createSelector(
    selectCurrentInbox,
    isQueueInbox,
    (inbox, isQueueInboxType) => {
      const channels: Array<string> = [];
      if (isQueueInboxType) {
        return channels;
      } else {
        if (inbox && inbox.sms) {
          channels.push('sms');
        }
        if (inbox && inbox.whatsapp) {
          channels.push('whatsapp');
        }
        return channels;
      }
    },
  );

export const selectCurrentInboxPhoneNumber: (arg1: State) => string =
  createSelector(
    selectCurrentInbox,
    isQueueInbox,
    (inbox, isQueueInboxType) => {
      if (isQueueInboxType) {
        return '';
      } else {
        // if it's not queue inbox and inbox is present - it will always be phone inbox and it will always have phone numbers
        // @ts-expect-error - TS2339 - Property 'phone_numbers' does not exist on type 'PhoneNumberSet | QueueBasedInbox'.
        return inbox ? inbox.phone_numbers?.[0] : '';
      }
    },
  );

// @ts-expect-error - TS2322 - Type 'OutputSelector<any, string[], (res1: PhoneNumberSet | QueueBasedInbox, res2: boolean, res3: string) => string[]>' is not assignable to type '() => string[]'.
export const selectAllInboxId: () => string[] = createSelector(
  selectCurrentInbox,
  isQueueInbox,
  selectCurrentInboxId,
  (inbox, isQueueInboxType, currentInboxId) => {
    if (isQueueInboxType) {
      return [inbox.id];
    } else {
      const inboxIds: Array<string> = [];
      if (inbox.sms) {
        inboxIds.push(inbox.sms.inbox_id);
      }
      if (inbox.whatsapp) {
        inboxIds.push(inbox.whatsapp.inbox_id);
      }
      !inboxIds.length && inboxIds.push(currentInboxId);
      return inboxIds;
    }
  },
);
